'use strict';

import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import {Map as ImmutableMap} from 'immutable';
import * as d3 from 'd3';
import moment from 'moment';
import numeral from 'numeral';

import TS from '@zerolight/zlthunder-time-utils';

import {encodeArray} from '../../shared/pocker-son';
import {cssClasses} from '../../shared/utils';
import {apiCall} from "../../shared/api-client";

import {useStatsState} from "./state";
import Cursor from './cursor';
import Legend from './legend';
import {safeNumber} from "../dashboard/shared";

// Padding
const CP = {top: 10, left: 10, right: 40, bottom: 30};

CP.hpadding = CP.left + CP.right;
CP.vpadding = CP.top + CP.bottom;

function sortSeries(p1, p2) {
	let t1 = TS.validate(p1.ts).valueOf();
	let t2 = TS.validate(p2.ts).valueOf();
	t1 = !isNaN(t1) ? t1 : 0;
	t2 = !isNaN(t2) ? t2 : 0;
	return t1 - t2;
}

function transformSeries(series, domain) {
	let warning = false;
	let re = [];
	series = Array.from(series);
	series.sort(sortSeries);
	for (let ts of domain) {
		ts = ts.valueOf();
		let value = 0;
		while (series.length) {
			let f = series[0];
			let fts = TS.validate(f.ts).valueOf();
			if (isNaN(fts)) {
				warning = true;
				series.shift();
				continue;
			}
			if (fts < ts) {
				series.shift();
				continue;
			}
			if (fts - ts < TS.DAY) {
				value += safeNumber(f.count);
				series.shift();
			} else {
				break;
			}
		}
		re.push({ts, value});
	}
	return re;
}

function DateTick({ts, tx}) {
	let dayOfMonth = ts.getUTCDate();
	let dayOfWeek = ts.getUTCDay();
	let weekend = dayOfWeek === 0 || dayOfWeek === 6;
	return (
		<g transform={`translate(${tx})`} className={cssClasses({weekend})}>
			<path d="M0,0V3"/>
			<text y="13">{ts.getUTCDate()}</text>
			{dayOfMonth === 1 && <text y="26">{moment(ts).utc().format('MMM')}</text>}
		</g>
	);
}

function XAxis({width, height, scale}) {
	return (
		<g className="x-axis" transform={`translate(${CP.left} ${height - CP.bottom})`}>
			<path d={`M0,0H${width}`}/>
			{
				scale
					.ticks(d3.utcDay)
					.map((ts) => <DateTick key={ts.toISOString()} ts={ts} tx={scale(ts)}/>)
			}
		</g>
	);
}

function YAxis({width, scale}) {
	const ticks = scale.ticks(6);
	if (ticks[0] === 0) {
		ticks.shift();
	}
	const axisWidth = width - CP.left;
	return (
		<g className="y-axis" transform={`translate(${CP.left} ${CP.bottom})`}>
			{
				ticks
					.map((v) => (
						<g key={`tick-${v.toFixed(2)}`} transform={`translate(0 ${scale(v)})`}>
							<path d={`M0,0H${axisWidth}`}/>
							<text transform={`translate(${axisWidth - 5} -3)`}>
								{numeral(v).format('0[.]0a')}
							</text>
						</g>
					))
			}
		</g>
	);
}

function StatsChart({width, height}) {
	const [state] = useStatsState();
	const [data, setData] = useState(null);
	const [cursor, setCursor] = useState(null);
	const [staple, setStaple] = useState(null);
	const onCursorChange = useCallback((ts) => setCursor(ts), [setCursor]);
	const onStapleSet = useCallback((ts) => setStaple(ts), [setStaple]);

	useEffect(() => {
		setData(null);
		let canceled = false;

		(async () => {
			let response = await apiCall({
				method: 'stats/series',
				params: {
					type: Array.from(state.get('nodes').values()).map(n => n.type),
					...state.get('filter').toObject()
				}
			});
			if (!canceled) {
				let data = new Map(response.data.map(s => [encodeArray(s.type), s]));
				let nodes = state.get('nodes');
				let sorted = new Map();
				for (let [k, node] of nodes.entries()) {
					let s = data.get(k);
					if (s) {
						s.node = node;
						sorted.set(k, s);
					}
				}
				setData(sorted);
			}
		})();

		return () => {
			canceled = true;
		};
	}, [state]);

	const chart = useMemo(() => {
		if (data === null) {
			return 'Waiting for data';
		}

		let timeRange = [Infinity, -Infinity];
		let valuesRange = [Infinity, -Infinity];
		let lines = [];
		for (let {series} of data.values()) {
			for (let {ts, count} of series) {
				ts = TS.validate(ts).valueOf();
				if (isNaN(ts)) {
					continue;
				}
				timeRange[0] = Math.min(timeRange[0], ts);
				timeRange[1] = Math.max(timeRange[1], ts);
				valuesRange[0] = Math.min(valuesRange[0], count);
				valuesRange[1] = Math.max(valuesRange[1], count);
			}
		}

		const chartWidth = width - CP.hpadding;
		const chartHeight = height - CP.vpadding;

		const xScale = d3.scaleUtc()
			.domain(timeRange)
			.range([0, chartWidth]);
		const yScale = d3.scaleLinear()
			.domain(valuesRange)
			.nice()
			.range([chartHeight, 0]);

		// Normalise series


		const line = d3.line()
			.x(d => xScale(d.ts))
			.y(d => yScale(d.value));

		let cc = 1;
		for (let {type, series} of data.values()) {
			series = transformSeries(series, xScale.ticks(d3.utcDay));
			series.sort(sortSeries);
			lines.push(<path d={line(series)} key={encodeArray(type)} className={`line zl-cc-stroke-${cc++}`}/>);
		}

		return <svg viewBox={`0,0,${width},${height}`}>
			<XAxis width={width} height={height} scale={xScale}/>
			<YAxis width={width} height={height} scale={yScale}/>
			<g transform={`translate(${CP.left} ${CP.top})`}>
				{lines}
			</g>
			<Cursor
				data={data}
				width={width} height={height} padding={CP}
				xScale={xScale} yScale={yScale}
				onCursorChange={onCursorChange}
				onStapleSet={onStapleSet}
			/>
		</svg>
	}, [data]);

	const legend = useMemo(() => {
		return <Legend data={data} cursor={cursor} staple={staple}/>;
	}, [data, cursor, staple]);

	return <>
		{chart}
		{legend}
	</>;
}

function Canvas() {
	const ref = useRef(null);
	const [view, setView] = useState(null);

	useEffect(() => {
		let c = ref.current;
		const width = c && c.offsetWidth;
		const height = c && c.offsetHeight;

		if (width && height) {
			setView(v => {
				v = v || new ImmutableMap({width: 0, height: 0});
				return v.withMutations((view) => {
					view.set('width', width);
					view.set('height', height);
				});
			});
		}
	}, [view]);

	if (!view) {
		console.debug('Detecting canvas size');
		return <div className="canvas" ref={ref}>Finding out the canvas size</div>
	}

	return (
		<div className="canvas cm-chart" ref={ref}>
			<StatsChart width={view.get('width')} height={view.get('height')}/>
		</div>
	);
}

export default Canvas;